import { getAnalytics, isSupported, logEvent, setConsent } from 'firebase/analytics';
import { app, firebaseConfig } from '../firebase';
import { getStoredConsent } from './cookieConsentService';

let analyticsPromise = null;
let analyticsInstance = null;
let isDefaultConsentSet = false;

/**
 * Google Consent Mode v2 の同意オブジェクトを構築する
 * @param {boolean} analyticsGranted
 */
const buildConsentParams = (analyticsGranted) => ({
  analytics_storage: analyticsGranted ? 'granted' : 'denied',
  ad_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied',
  functionality_storage: 'granted',
  security_storage: 'granted',
});

/**
 * gtag ヘルパーおよび Consent Mode default の事前初期化を保証する
 */
const ensureConsentDefault = (initialConsented = false) => {
  if (typeof window === 'undefined') return;

  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== 'function') {
    window.gtag = function () {
      window.dataLayer.push(arguments);
    };
  }

  if (!isDefaultConsentSet) {
    window.gtag('consent', 'default', {
      ...buildConsentParams(initialConsented),
      wait_for_update: 500,
    });
    isDefaultConsentSet = true;
  }
};

/**
 * Update consent settings in Google Analytics (Consent Mode v2)
 * @param {boolean} granted - Whether analytics cookies/storage is consented
 */
export const updateAnalyticsConsent = (granted) => {
  try {
    ensureConsentDefault(granted);
    const consentParams = buildConsentParams(granted);

    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('consent', 'update', consentParams);
    }

    setConsent(consentParams);
  } catch (error) {
    console.error('Failed to update analytics consent:', error);
  }
};

/**
 * Initialize analytics services with Consent Mode v2
 * @param {boolean} [consented] - Initial consent status (if omitted, reads from stored consent)
 */
export const initializeAnalytics = async (consented) => {
  const stored = getStoredConsent();
  const isGranted =
    typeof consented === 'boolean'
      ? consented
      : Boolean(stored && stored.analytics);

  // 1. getAnalytics(app) が呼ばれる前に必ず consent default と setConsent を設定する
  ensureConsentDefault(isGranted);
  const consentParams = buildConsentParams(isGranted);
  setConsent(consentParams);

  if (analyticsInstance) {
    updateAnalyticsConsent(isGranted);
    return analyticsInstance;
  }
  if (analyticsPromise) {
    updateAnalyticsConsent(isGranted);
    return analyticsPromise;
  }

  analyticsPromise = (async () => {
    try {
      if (typeof window === 'undefined') return null;
      if (!firebaseConfig.measurementId) {
        console.warn('Google Analytics Measurement ID is not configured.');
        return null;
      }

      const supported = await isSupported().catch(() => false);
      if (!supported) return null;

      // 2. デフォルト同意がセットされた状態で Analytics インスタンスを生成
      analyticsInstance = getAnalytics(app);

      // 3. 最新の同意状態を明示的に update
      updateAnalyticsConsent(isGranted);

      return analyticsInstance;
    } catch (error) {
      console.error('Failed to initialize analytics:', error);
      return null;
    }
  })();

  return analyticsPromise;
};

/**
 * Log page view or custom event safely
 */
export const logAnalyticsEvent = async (eventName, eventParams = {}) => {
  try {
    const instance =
      analyticsInstance ||
      (await analyticsPromise) ||
      (await initializeAnalytics());
    if (!instance) return;
    logEvent(instance, eventName, eventParams);
  } catch (error) {
    // Ignore tracking errors safely
  }
};

/**
 * Check if analytics is currently initialized
 */
export const isAnalyticsInitialized = () => Boolean(analyticsInstance);
